import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import api from '../../config/axios';

import { fetchABOFileById } from '../../store/slices/aboSlice';
import { fetchInvoices } from '../../store/slices/invoiceSlice';
import { RootState, AppDispatch } from '../../store/store';
import czechTranslations from '../../translations/czech';
import useModernAlert from '../../hooks/useModernAlert';

const aboStatusLabels = {
  generated: 'Vytvořeno',
  submitted: 'Odesláno',
  sent: 'Odesláno',
  sent_to_fio: 'Odesláno do FIO',
  error: 'Chyba'
} as const;

const invoiceStatusLabels = {
  new: 'Nový',
  pending: 'Čeká na zpracování',
  reviewed: 'Zkontrolováno',
  ready_for_payment: 'Připraveno k platbě',
  sent_to_bank: 'Odesláno do banky',
  paid: 'Zaplaceno',
  rejected: 'Zamítnuto'
} as const;

const bankColors = {
  FIO: 'primary',
  CSOB: 'secondary',
  KB: 'success'
} as const;

const bankLabels = {
  FIO_ABO: 'FIO ABO',
  FIO_API: 'FIO API',
  CSOB: 'ČSOB',
  KB: 'Komerční banka'
} as const;

const ABODetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { showError, showSuccess, AlertComponent } = useModernAlert();
  const { currentABOFile, loading, error } = useSelector((state: RootState) => state.abo);
  
  // Bank account selection state
  const [bankAccountDialogOpen, setBankAccountDialogOpen] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');
  const [sendingToFIO, setSendingToFIO] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchABOFileById(parseInt(id)));
    }
  }, [dispatch, id]);

  const handleDownloadABO = async () => {
    if (!currentABOFile) return;

    try {
      const response = await api.get(`/abo/${currentABOFile.id}/download`, {
        responseType: 'blob'
      });

      if (response.status === 200) {
        // Get the filename from the response headers or use the current filename
        const contentDisposition = response.headers['content-disposition'];
        let filename = currentABOFile.filename || `ABO_${currentABOFile.id}.txt`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Create blob and download
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Download failed:', response.statusText);
        showError(czechTranslations.alerts.downloadFailed);
      }
    } catch (error) {
      console.error('Download error:', error);
      showError(czechTranslations.alerts.downloadError);
    }
  };

  const handleSendToFIO = async () => {
    if (!currentABOFile) return;

    try {
      // First, get available FIO bank accounts
      const response = await api.post(`/abo/${currentABOFile.id}/send-to-fio`, {});

      if (response.status === 200) {
        const data = response.data;
        if (data.availableAccounts) {
          // Show bank account selection dialog
          setAvailableAccounts(data.availableAccounts);
          setBankAccountDialogOpen(true);
        } else {
          // Direct response (shouldn't happen with current logic)
          showSuccess(data.message);
          dispatch(fetchABOFileById(parseInt(id!)));
        }
      }
    } catch (error: any) {
      console.error('Send error:', error);
      
      const errorData = error.response?.data;
      const errorType = errorData?.type;
      const errorMessage = errorData?.error || 'Načtení bankovních účtů selhalo';
      const errorDetails = errorData?.details;
      const detailedErrors = errorData?.detailedErrors;
      const errorCode = errorData?.errorCode;
      
      let displayMessage = '';
      
      switch (errorType) {
        case 'bank_validation_error':
        case 'validation_error':
          displayMessage = `❌ Chyba validace\n\n${errorMessage}`;
          if (errorDetails) {
            displayMessage += `\n\n${errorDetails}`;
          }
          break;
          
        case 'fio_api_error':
          displayMessage = `❌ Chyba FIO API${errorCode ? ` (kód ${errorCode})` : ''}\n\n${errorMessage}`;
          
          // Show detailed per-transaction errors
          if (detailedErrors && Array.isArray(detailedErrors) && detailedErrors.length > 0) {
            displayMessage += '\n\nChyby u jednotlivých plateb:';
            detailedErrors.forEach((err: any) => {
              displayMessage += `\n• VS ${err.vs} (${err.account}/${err.bankCode}): ${err.error}`;
            });
          }
          break;
          
        default:
          // Even without a type, show the full error message (which may contain detailed validation errors)
          displayMessage = errorMessage;
          if (errorDetails) {
            displayMessage += `\n\n${errorDetails}`;
          }
      }
      
      showError(displayMessage);
    }
  };

  const handleConfirmSendToFIO = async () => {
    if (!currentABOFile || !selectedBankAccountId) return;

    setSendingToFIO(true);
    try {
      const response = await api.post(`/abo/${currentABOFile.id}/send-to-fio`, { bankAccountId: selectedBankAccountId });

      if (response.status === 200) {
        const result = response.data;
        
        // Show detailed success message with past payment info - translate the FIO success message
        let successMessage;
        if (result.message.includes('Payments sent to FIO successfully')) {
          // Extract any additional info (like past payments note) from the original message
          const additionalInfo = result.message.replace('Payments sent to FIO successfully', '').trim();
          successMessage = `✅ ${czechTranslations.alerts.paymentsSentToFioSuccessfully}${additionalInfo}`;
        } else {
          successMessage = `✅ ${result.message}`;
        }
        if (result.pastPayments && result.pastPayments.length > 0) {
          successMessage += `\n\n⚠️ Poznámka: ${result.pastPayments.length} platba(y) s minulým datem splatnosti byla(y) upravena(y) na dnešní datum pro FIO API.`;
        }
        
        showSuccess(successMessage);
        setBankAccountDialogOpen(false);
        setSelectedBankAccountId('');
        // Refresh the ABO file data
        dispatch(fetchABOFileById(parseInt(id!)));
        dispatch(fetchInvoices({}));
      }
    } catch (error: any) {
      console.error('Send error:', error);
      
      const errorData = error.response?.data;
      const errorType = errorData?.type;
      const errorMessage = errorData?.error || 'Odeslání selhalo';
      const errorDetails = errorData?.details;
      const detailedErrors = errorData?.detailedErrors;
      const errorCode = errorData?.errorCode;
      
      let displayMessage = '';
      
      switch (errorType) {
        case 'bank_validation_error':
          displayMessage = `❌ Chyba validace bankovních účtů\n\n${errorMessage}`;
          if (errorDetails) {
            displayMessage += `\n\n${errorDetails}`;
          }
          break;
          
        case 'fio_api_error':
          displayMessage = `❌ Chyba FIO API${errorCode ? ` (kód ${errorCode})` : ''}\n\n${errorMessage}`;
          
          // Show detailed per-transaction errors
          if (detailedErrors && Array.isArray(detailedErrors) && detailedErrors.length > 0) {
            displayMessage += '\n\nChyby u jednotlivých plateb:';
            detailedErrors.forEach((err: any) => {
              displayMessage += `\n• VS ${err.vs} (${err.account}/${err.bankCode}): ${err.error}`;
            });
          }
          break;
          
        case 'bank_account_missing':
          displayMessage = `❌ ${errorMessage}\n\nMusíte nejprve přidat bankovní účet s FIO tokenem.`;
          break;
          
        case 'no_invoices':
          displayMessage = `⚠️ ${errorMessage}`;
          break;
          
        default:
          // Even without a type, show the full error message (which may contain detailed validation errors)
          displayMessage = errorMessage;
          if (errorDetails) {
            displayMessage += `\n\n${errorDetails}`;
          }
      }
      
      showError(displayMessage);
    } finally {
      setSendingToFIO(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    // Extract error message from object or string
    const errorMessage = typeof error === 'string' ? error : error?.error || 'Neznámá chyba';
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  if (!currentABOFile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">ABO soubor nebyl nalezen</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/abo')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            ABO soubor: {currentABOFile.filename}
          </Typography>
          <Chip
            label={bankLabels[currentABOFile.bankType as keyof typeof bankLabels] || currentABOFile.bankType}
            color={bankColors[currentABOFile.bankType as keyof typeof bankColors] || 'default'}
          />
          <Chip
            label={aboStatusLabels[currentABOFile.status as keyof typeof aboStatusLabels] || 'Vytvořeno'}
            color={currentABOFile.status === 'sent' || currentABOFile.status === 'submitted' ? 'success' : 'default'}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentABOFile.bankType === 'FIO_API' ? (
            // For FIO API files, show Send via API button instead of download
            currentABOFile.status !== 'sent' ? (
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendToFIO}
              >
                Odeslat via FIO API...
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                disabled
              >
                Odesláno via FIO API
              </Button>
            )
          ) : (
            // For other files, show download button (allow re-downloads)
            currentABOFile.status !== 'sent' ? (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadABO}
              >
                Stáhnout ABO soubor
              </Button>
            ) : (
              <Chip
                label={aboStatusLabels[currentABOFile.status as keyof typeof aboStatusLabels] || 'Odesláno'}
                color="success"
                size="small"
              />
            )
          )}
        </Box>
      </Box>

      {/* Basic Information */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Základní informace
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Název souboru:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {currentABOFile.filename}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Banka:
                  </Typography>
                  <Typography variant="body1">
                    {bankLabels[currentABOFile.bankType as keyof typeof bankLabels] || currentABOFile.bankType}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Počet faktur:
                  </Typography>
                  <Typography variant="body1">
                    {currentABOFile.invoiceCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Celková částka:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {currentABOFile.totalAmount?.toLocaleString('cs-CZ')} Kč
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Vytvořeno:
                  </Typography>
                  <Typography variant="body1">
                    {currentABOFile.createdAt && !isNaN(new Date(currentABOFile.createdAt).getTime())
                      ? format(new Date(currentABOFile.createdAt), 'dd.MM.yyyy HH:mm', { locale: cs })
                      : 'N/A'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status:
                  </Typography>
                  <Typography variant="body1">
                    {aboStatusLabels[currentABOFile.status as keyof typeof aboStatusLabels] || currentABOFile.status}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistika
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Počet transakcí:
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {currentABOFile.transactions?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Průměrná částka:
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {currentABOFile.transactions && currentABOFile.transactions.length > 0
                      ? (currentABOFile.totalAmount / currentABOFile.transactions.length).toLocaleString('cs-CZ')
                      : 0} Kč
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Nejvyšší částka:
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {currentABOFile.transactions
                      ? Math.max(...currentABOFile.transactions.map((t: any) => t.amount || 0)).toLocaleString('cs-CZ')
                      : 0} Kč
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transactions */}
      <Paper sx={{ mb: 3 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Transakce ({currentABOFile.transactions?.length || 0})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Číslo faktury</TableCell>
                    <TableCell>Dodavatel</TableCell>
                    <TableCell>Číslo účtu</TableCell>
                    <TableCell>IBAN</TableCell>
                    <TableCell align="right">Částka</TableCell>
                    <TableCell>Variabilní symbol</TableCell>
                    <TableCell>Zpráva pro příjemce</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentABOFile.transactions?.map((transaction: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{transaction.invoice_number}</TableCell>
                      <TableCell>{transaction.supplier_name}</TableCell>
                      <TableCell>{transaction.account_number}</TableCell>
                      <TableCell>{transaction.iban}</TableCell>
                      <TableCell align="right">
                        {transaction.amount?.toLocaleString('cs-CZ')} Kč
                      </TableCell>
                      <TableCell>{transaction.variable_symbol}</TableCell>
                      <TableCell>{transaction.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Related Invoices */}
      <Paper>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Související faktury ({currentABOFile.invoices?.length || 0})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {currentABOFile.invoices?.map((invoice: any) => (
                <Grid item xs={12} md={6} key={invoice.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6">
                          {invoice.invoice_number}
                        </Typography>
                        <Chip
                          label={invoiceStatusLabels[invoice.status as keyof typeof invoiceStatusLabels] || invoice.status}
                          size="small"
                          color="primary"
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {invoice.supplier_name}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {invoice.total_amount?.toLocaleString('cs-CZ')} Kč
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Datum: {invoice.invoice_date ? (() => {
                          const date = new Date(invoice.invoice_date);
                          return isNaN(date.getTime()) ? 'N/A' : format(date, 'dd.MM.yyyy', { locale: cs });
                        })() : 'N/A'}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => navigate(`/invoices/${invoice.id}`, { 
                            state: { from: 'abo', aboId: currentABOFile.id } 
                          })}
                        >
                          Zobrazit detail
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* ABO File Content Preview */}
      {currentABOFile.content && (
        <Paper sx={{ mt: 3 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Náhled ABO souboru
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace'
                }}
              >
                {currentABOFile.content}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      {/* Bank Account Selection Dialog */}
      <Dialog open={bankAccountDialogOpen} onClose={() => setBankAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Vyberte FIO bankovní účet
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Vyberte bankovní účet, ze kterého budou odeslány platby via FIO API:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Bankovní účet</InputLabel>
            <Select
              value={selectedBankAccountId}
              onChange={(e) => setSelectedBankAccountId(e.target.value)}
              label="Bankovní účet"
            >
              {availableAccounts.map((account: any) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.accountName} - {account.accountNumber}/{account.bankCode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankAccountDialogOpen(false)}>
            Zrušit
          </Button>
          <Button
            onClick={handleConfirmSendToFIO}
            variant="contained"
            disabled={!selectedBankAccountId || sendingToFIO}
            startIcon={sendingToFIO ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingToFIO ? 'Odesílám...' : 'Odeslat via FIO API'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {AlertComponent}
    </Box>
  );
};

export default ABODetail; 